import React from 'react';
import { Search, Sparkles } from 'lucide-react';
import { useCases, useCaseCategories, type UseCaseCategory } from './useCasesData';

const KIND_LABEL: Record<string, string> = {
  'image-edit': 'Photo in',
  'image-gen': 'Prompt to image',
  'remove-bg': 'Photo in',
  tts: 'Text to speech',
  music: 'Text to music',
  sfx: 'Text to SFX',
  video: 'Text to video',
  'image-video': 'Photo to video',
};

export const UseCaseDirectory: React.FC = () => {
  const [query, setQuery] = React.useState('');
  const [category, setCategory] = React.useState<UseCaseCategory | 'All'>('All');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return useCases.filter((u) => {
      if (category !== 'All' && u.category !== category) return false;
      if (!q) return true;
      return (
        u.title.toLowerCase().includes(q) ||
        u.tagline.toLowerCase().includes(q) ||
        u.keywords.some((k) => k.includes(q)) ||
        u.category.toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  return (
    <main className="px-4 pb-24 pt-28 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <header className="mb-8 max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-mono text-white/45">
            <Sparkles size={13} /> {useCases.length} AI tools, one credit balance
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl">AI tools for every job</h1>
          <p className="mt-4 text-base leading-relaxed text-white/55">
            Job-specific AI tools on the app.nz model gateway: stage listing photos, make product images without a
            studio, restore family photos, generate voiceovers, music and video. Sign in, spend credits, download the
            result — every tool is also an API.
          </p>
        </header>

        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="relative flex-1">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools — staging, headshot, voiceover…"
              className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none"
            />
          </label>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {(['All', ...useCaseCategories] as const).map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                category === c
                  ? 'border-sky-400/50 bg-sky-400/10 text-sky-300'
                  : 'border-white/10 bg-white/[0.02] text-white/55 hover:border-white/25 hover:text-white'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((u) => (
            <a
              key={u.slug}
              href={`/ai/${u.slug}`}
              className="group rounded-xl border border-white/10 bg-white/[0.02] p-5 transition hover:border-white/25 hover:bg-white/[0.04]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-white/35">{u.category}</span>
                <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-white/40">
                  {KIND_LABEL[u.kind]}
                </span>
              </div>
              <h2 className="mt-2 text-base font-semibold text-white group-hover:text-sky-300">{u.title}</h2>
              <p className="mt-1.5 text-sm leading-5 text-white/50">{u.tagline}</p>
            </a>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="py-16 text-center text-white/40">No tools match that search.</p>
        ) : null}
      </div>
    </main>
  );
};
